/**
 * Redacts domain names from a URL-like string, api://some/text, https://other/text or otherwise.
 * Assumes a fully formed URL with protocol, such as api://url.com or api://url.com/scope.
 * Returns string unmodified in all other cases.
 *
 * @param url URL string to redact domain from. Non-URL text is fine and will be returned unmodified.
 * @returns Redacted domain or unmodified string, if no domain was detected
 */ export function redactDomain(url) {
    // Sample Input:
    //    api://749d0000-0194-1005-2e05-08d61613bf2f/SomeScope.All
    // Capture Groups:
    //    protocol: api://
    //      domain: 749d0000-0194-1005-2e05-08d61613bf2f
    //   remaining: /SomeScope.All
    const domainMatcher = /([a-zA-Z\d-]+:\/\/)([a-zA-Z\d-\.]+)(\/.*)*$/;
    let redactedDomain = url;
    const matches = domainMatcher.exec(url);
    if (matches && matches.length > 2) {
        const protocol = matches[1];
        const remaining = matches[3] ? matches[3] : '';
        redactedDomain = `${protocol}(redacted)${remaining}`;
    }
    return redactedDomain;
}
/**
 * Helper for running redactDomain on string arrays.
 *
 * @param urls URLs to redact.
 * @returns Redacted URLs or unmodified input, as applicable.
 * @see redactDomain
 */ export function redactDomains(urls) {
    return urls.map((url)=>redactDomain(url));
}
/**
 * Redacts sharepoint domain substring of the format https://[example]-my.sharepoint.com
 * Returns string unmodified in all other cases.
 *
 * @param text String that may contain sharepoint domain information
 * @returns Sanitized error stack where the sharepoint domain and/or page url has been replaced with `redacted`
 * // TODO: Task 7173656: Redact any url except for .js file
 */ export function redactSharepointDomainAndLoopPageUrl(text) {
    // We do not want to redact other domain strings like https://office.com, etc since they can help with diagnostics - de-minifying call stacks
    // But this specific substring needs to be redacted since domain name is OII and should not be logged.
    const spDomainRegex = /(https:\/\/)(\w)+(-my.sharepoint.com)/;
    const pageUrlRegex = /(https:\/\/)(dev.loop.microsoft.com|df.loop.microsoft.com|loop.microsoft.com)(\/p\/)([a-zA-Z0-9%]+)/;
    const redactedString = text.replace(spDomainRegex, '(redacted)');
    return redactedString.replace(pageUrlRegex, '(redacted)');
}
